import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import * as React from 'react';
import { ExternalOrderService } from 'services';
import { SnackbarContext } from 'components';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface HoursRow {
  id?: number;
  dayOfWeek: number;
  fromTime: string | null;
  toTime: string | null;
}

const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const DEFAULT_FROM = '10:00:00';
const DEFAULT_TO = '22:00:00';

const trimSeconds = (s: string | null): string => {
  if (!s) return '';
  return s.length >= 5 ? s.substring(0, 5) : s;
};

const addSeconds = (s: string): string =>
  s.length === 5 ? `${s}:00` : s;

export const BoltHoursDialog: React.FC<Props> = ({ open, onClose }) => {
  const snackbar = React.useContext(SnackbarContext);
  const [rows, setRows] = React.useState<HoursRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    ExternalOrderService.getBoltOpeningHours()
      .then((resp) => {
        const byDay: Record<number, HoursRow> = {};
        (resp || []).forEach(
          (r: {
            id: number;
            dayOfWeek: number;
            fromTime: string | null;
            toTime: string | null;
          }) => {
            byDay[r.dayOfWeek] = {
              id: r.id,
              dayOfWeek: r.dayOfWeek,
              fromTime: r.fromTime,
              toTime: r.toTime,
            };
          },
        );
        const allDays: HoursRow[] = Array.from({ length: 7 }, (_, d) => {
          return (
            byDay[d] || {
              dayOfWeek: d,
              fromTime: null,
              toTime: null,
            }
          );
        });
        setRows(allDays);
      })
      .catch((e) => snackbar.error(e))
      .finally(() => setLoading(false));
  }, [snackbar]);

  React.useEffect(() => {
    if (open) {
      load();
    }
  }, [open, load]);

  const setRowField = (idx: number, key: keyof HoursRow, value: any) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save each row sequentially so a save error doesn't leave partial
      // state behind; with only 7 rows this is fast enough.
      for (const r of rows) {
        const isOpen = !!(r.fromTime && r.toTime);
        if (isOpen) {
          await ExternalOrderService.saveBoltOpeningHour({
            id: r.id,
            dayOfWeek: r.dayOfWeek,
            fromTime: r.fromTime,
            toTime: r.toTime,
          });
        } else if (r.id) {
          // Was open before, now closed — delete the row.
          await ExternalOrderService.deleteBoltOpeningHour(r.id);
        }
      }
      snackbar.open('Bolt opening hours saved');
      onClose();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bolt opening hours</DialogTitle>
      <DialogContent>
        <DialogContentText style={{ marginBottom: 12 }}>
          When you click "Sync Hours" on the status bar, these times are
          pushed to Bolt as your working hours. Leave a day's checkbox
          unticked to mark it closed.
        </DialogContentText>
        {loading && <Typography variant="caption">Loading…</Typography>}
        {!loading &&
          rows.map((r, idx) => {
            const isOpen = !!(r.fromTime && r.toTime);
            return (
              <Grid
                container
                key={r.dayOfWeek}
                spacing={1}
                alignItems="center"
                style={{ marginBottom: 6 }}
              >
                <Grid item xs={1}>
                  <Checkbox
                    checked={isOpen}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setRows((prev) =>
                          prev.map((p, i) =>
                            i === idx
                              ? {
                                  ...p,
                                  fromTime: p.fromTime || DEFAULT_FROM,
                                  toTime: p.toTime || DEFAULT_TO,
                                }
                              : p,
                          ),
                        );
                      } else {
                        setRows((prev) =>
                          prev.map((p, i) =>
                            i === idx
                              ? { ...p, fromTime: null, toTime: null }
                              : p,
                          ),
                        );
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2">
                    {DAY_LABELS[r.dayOfWeek]}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    type="time"
                    label="From"
                    fullWidth
                    disabled={!isOpen}
                    value={trimSeconds(r.fromTime)}
                    onChange={(e) =>
                      setRowField(idx, 'fromTime', addSeconds(e.target.value))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    type="time"
                    label="To"
                    fullWidth
                    disabled={!isOpen}
                    value={trimSeconds(r.toTime)}
                    onChange={(e) =>
                      setRowField(idx, 'toTime', addSeconds(e.target.value))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </Grid>
              </Grid>
            );
          })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
